import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Eye, EyeOff, ArrowLeft, Truck } from 'lucide-react';
import { getUserErrorMessage } from '../../utils/errorMessages';

const SPOOKER_LOGO = "https://spooker.io/wp-content/uploads/2022/12/logo.svg";

const SupplierLoginPage = () => {
  const { login, verifyMFA } = useSupplierAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.mfa_required) {
        setMfaRequired(true);
        setMfaToken(result.mfa_token);
        toast.info('Voer uw verificatiecode in');
      } else {
        toast.success('Ingelogd als leverancier');
        navigate('/leverancier');
      }
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Inloggen mislukt'));
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await verifyMFA(email, mfaToken, mfaCode);
      toast.success('Ingelogd als leverancier');
      navigate('/leverancier');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Verificatie mislukt'));
    } finally {
      setLoading(false);
    }
  };

  if (mfaRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-950 dark:to-neutral-900 p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={SPOOKER_LOGO} alt="Spooker" className="h-8 mx-auto mb-4 dark:invert" />
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
              <Truck className="h-4 w-4" />
              Leverancier Portal
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Tweestapsverificatie</CardTitle>
              <CardDescription>
                Voer de code in van uw authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleMFAVerify} className="space-y-4">
                <div>
                  <Label htmlFor="mfaCode">Verificatiecode</Label>
                  <Input
                    id="mfaCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest"
                    data-testid="mfa-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || mfaCode.length !== 6}
                  data-testid="mfa-verify-btn"
                >
                  {loading ? 'Verifiëren...' : 'Verifiëren'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setMfaRequired(false);
                    setMfaCode('');
                  }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Terug
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-950 dark:to-neutral-900 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={SPOOKER_LOGO} alt="Spooker" className="h-8 mx-auto mb-4 dark:invert" />
          <div className="flex items-center justify-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
            <Truck className="h-4 w-4" />
            Leverancier Portal
          </div>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Inloggen</CardTitle>
            <CardDescription>
              Log in op uw leveranciersaccount
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">E-mailadres</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="uw@bedrijf.nl"
                  required
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Wachtwoord</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-btn"
              >
                {loading ? 'Inloggen...' : 'Inloggen'}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
              <p className="text-sm text-center text-neutral-600 dark:text-neutral-400">
                Nog geen account?{' '}
                <Link to="/leverancier/register" className="text-black dark:text-white font-medium hover:underline">
                  Registreer als leverancier
                </Link>
              </p>
            </div>
            
            <div className="mt-4">
              <Link 
                to="/login" 
                className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center justify-center gap-1"
              >
                <ArrowLeft className="h-3 w-3" />
                Terug naar klant login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupplierLoginPage;
